import { useSelector } from 'react-redux';

import {
  BINARY_DECOMPOSITION,
  ENABLE_REACHABILITY,
  FULL_SECURITY,
  SNIPPET_DETECTION,
} from '../../../../shared/featureFlags/flagNames';
import { BinaryFetcherName } from '../../../../shared/fetchers';
import { Category, IssueFilterKeys, IssueScopeKind } from '../../../../shared/issuesV2';
import Locator from '../../../../shared/Locator';
import { isFreeTier, PREMIUM_COMP_SEC_PLAN_NAME, PREMIUM_COMPLIANCE_PLAN_NAME } from '../../../../shared/subscriptions';
import { window } from '../../../globals';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { useReleaseGroupAllProjects } from '../../../hooks/api/releaseGroups';

import './Filters.less';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { useSetFilterHasChanged } from '../../../redux/savedFilters/hooks';
import { FeatureFlagRequired } from '../../generic';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import SavedFilters from '../../savedFilters';
import FossabotAlphaAd from '../../shared/ads/fossabotAlphaAd';
import useFetchIssuesAction from '../IssuesInbox/hooks/useFetchIssuesAction';
import type { EpssFilter, LicensingFilter, QualityFilter, VulnFilter } from '../../../../shared/issuesV2';

import ConfidenceIssueFilter from './ConfidenceIssueFilter';
import ContainerLayerFilter from './ContainerLayerFilter';
import DepthIssueFilter from './DepthIssueFilter';
import FoundAfterFilter from './FoundAfterFilter';
import IssueSourceFilter from './IssueSourceFilter';
import IssueTypeFilter from './IssueTypeFilter';
import LicenseIdentificationFilter from './LicenseIdentificationFilter';
import LicenseIDFilter from './LicenseIDFilter';
import PackageManagerFilter from './PackageManagerFilter';
import ProjectLabelsFilter from './ProjectLabelsFilter';
import QualityIssueFilter from './QualityIssueFilter';
import TicketIssueFilter from './TicketIssueFilter';
import CweFilter from './VulnerabilityIssueFilter/CweFilter';
import EPSSRangeFilter from './VulnerabilityIssueFilter/EPSSRangeFilter';
import ExploitMaturityFilter from './VulnerabilityIssueFilter/ExploitMaturityFilter';
import IgnoreReasonFilter from './VulnerabilityIssueFilter/IgnoreReasonFilter';
import ReachabilityFilter from './VulnerabilityIssueFilter/ReachabilityFilter';
import SeverityFilter from './VulnerabilityIssueFilter/SeverityFilter';
import UpgradeDistanceFilter from './VulnerabilityIssueFilter/UpgradeDistanceFilter';

const IssuesFilters = () => {
  const setFilters = useFetchIssuesAction(IssuesV2Store.actions.setFilter);
  const stateFilters = useSelector(IssuesV2Store.selectors.getFilter);
  const issueCategory = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const projectType = useSelector(IssuesV2Store.selectors.getProjectType);
  const issueStatus = useSelector(IssuesV2Store.selectors.getStatus);
  const issueScope = useSelector(IssuesV2Store.selectors.getScope);
  const licenseList = useSelector(IssuesV2Store.selectors.getIssueLicenseList);

  const setFilterHasChanged = useSetFilterHasChanged();

  const isSecurityRestricted = !useIsFeatureEnabled(FULL_SECURITY);
  const isBinaryDecompEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);

  const releaseGroupId = issueScope.type === IssueScopeKind.ReleaseGroup ? Number(issueScope.id) : -1;
  const {
    data: releaseProjects,
  } = useReleaseGroupAllProjects(releaseGroupId, isBinaryDecompEnabled && releaseGroupId > 0);
  const isReleaseGroupWithBinaryProject = releaseProjects?.some(releaseProject => {
    return Locator.parse(releaseProject).fetcher === BinaryFetcherName;
  });

  const shouldShowConfidenceFilter = isBinaryDecompEnabled && (
    (issueScope.type === IssueScopeKind.ReleaseGroup && isReleaseGroupWithBinaryProject) ||
    (issueScope.type === IssueScopeKind.Project && projectType === 'binary')
  );

  // utility function for selecting items in Checkbox filter group
  const onSelect = (filterKey: IssueFilterKeys) => (key: string, selected: boolean) => {
    const existingOptions = (
      filterKey in stateFilters ? stateFilters[filterKey as keyof typeof stateFilters] : []
    ) as string[];

    let newOptions: string[];

    if (selected) {
      // If the filter option (key) was selected, a new array is created including this option.
      newOptions = [...existingOptions, key];
    } else {
      // Otherwise, a new array is created without the unselected filter option (key)
      newOptions = (existingOptions).filter((selectedOption: string) => selectedOption !== key);
    }
    setFilters({ ...stateFilters, [filterKey]: newOptions });
    setFilterHasChanged(true);
  };

  // utility function for selecting items in MultiSelect filter group.
  const onSet = (filterKey: IssueFilterKeys) => (selectedValues: string[]) => {
    setFilters({ ...stateFilters, [filterKey]: selectedValues });
    setFilterHasChanged(true);
  };

  //  For setting the EPSS filter, which is a Range selector + a radio button to select the type of EPSS.
  //  If `null` is passed, it means the EPSS filter is being removed.
  const onSetEPSS = (epssFilter: EpssFilter | null) => {
    if (epssFilter === null) {
      return setFilters({
        ...stateFilters,
        [IssueFilterKeys.Epss]: undefined,
      });
    }
    setFilters({
      ...stateFilters,
      [IssueFilterKeys.Epss]: epssFilter,
    });
    return setFilterHasChanged(true);
  };

  const getFiltersForProjectType = () => {
    switch (projectType) {
      case 'container':
        return (
          <ContainerLayerFilter
            onSelect={onSelect(IssueFilterKeys.ContainerLayers)}
            selectedOptions={stateFilters[IssueFilterKeys.ContainerLayers]}
          />
        );
      default:
        return null;
    }
  };

  const getFiltersForCurrentCategory = () => {
    switch (issueCategory) {
      case Category.Licensing:
        return (
          <>
            <IssueTypeFilter
              onSelect={onSelect(IssueFilterKeys.Type)}
              selectedOptions={(stateFilters as LicensingFilter)[IssueFilterKeys.Type]}
            />
            <LicenseIdentificationFilter
              onSelect={onSelect(IssueFilterKeys.Identification)}
              selectedOptions={(stateFilters as LicensingFilter)[IssueFilterKeys.Identification]}
            />
            <LicenseIDFilter
              onSet={onSet(IssueFilterKeys.Licenses)}
              selectedOptions={(stateFilters as LicensingFilter)[IssueFilterKeys.Licenses] || []}
              licenses={licenseList}
            />
            <FeatureFlagRequired flagName={SNIPPET_DETECTION}>
              <IssueSourceFilter
                onSelect={onSelect(IssueFilterKeys.IssueSource)}
                selectedOptions={(stateFilters as LicensingFilter)[IssueFilterKeys.IssueSource]}
              />
            </FeatureFlagRequired>
          </>
        );

      case Category.Vulnerability:
        return (
          <>
            <CweFilter />
            <SeverityFilter
              onSelect={onSelect(IssueFilterKeys.Severity)}
              selectedOptions={(stateFilters as VulnFilter)[IssueFilterKeys.Severity]}
            />
            <FeatureFlagRequired flagName={ENABLE_REACHABILITY}>
              <ReachabilityFilter
                onSelect={onSelect(IssueFilterKeys.Reachability)}
                selectedOptions={(stateFilters as VulnFilter)[IssueFilterKeys.Reachability]}
              />
            </FeatureFlagRequired>
            <PaidFeatureGate isRestricted={isSecurityRestricted} planToShow={PREMIUM_COMP_SEC_PLAN_NAME}>
              <UpgradeDistanceFilter
                onSelect={onSelect(IssueFilterKeys.UpgradeDistance)}
                selectedOptions={(stateFilters as VulnFilter)[IssueFilterKeys.UpgradeDistance]}
              />
            </PaidFeatureGate>
            <PaidFeatureGate isRestricted={isSecurityRestricted} planToShow={PREMIUM_COMP_SEC_PLAN_NAME}>
              <ExploitMaturityFilter
                onSelect={onSelect(IssueFilterKeys.ExploitMaturity)}
                selectedOptions={(stateFilters as VulnFilter)[IssueFilterKeys.ExploitMaturity]}
              />
            </PaidFeatureGate>
            <PaidFeatureGate isRestricted={isSecurityRestricted} planToShow={PREMIUM_COMP_SEC_PLAN_NAME}>
              <EPSSRangeFilter
                filter={(stateFilters as VulnFilter)[IssueFilterKeys.Epss]}
                onChange={onSetEPSS}
              />
            </PaidFeatureGate>
            <FeatureFlagRequired flagName={SNIPPET_DETECTION}>
              <IssueSourceFilter
                onSelect={onSelect(IssueFilterKeys.IssueSource)}
                selectedOptions={(stateFilters as VulnFilter)[IssueFilterKeys.IssueSource]}
              />
            </FeatureFlagRequired>
            {issueStatus === 'ignored' && (
              <IgnoreReasonFilter
                onSet={onSet(IssueFilterKeys.IgnoreReason)}
                selectedOptions={(stateFilters as VulnFilter)[IssueFilterKeys.IgnoreReason]}
              />
            )}
          </>
        );

      case Category.Quality:
        return (
          <QualityIssueFilter
            onSelect={onSelect(IssueFilterKeys.Type)}
            selectedOptions={(stateFilters as QualityFilter)[IssueFilterKeys.Type]}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="issues-filter">
      <SavedFilters />
      {issueCategory === Category.Vulnerability && (
        <FossabotAlphaAd />
      )}
      <PackageManagerFilter />
      {issueScope.type === IssueScopeKind.Global && <ProjectLabelsFilter />}
      <DepthIssueFilter
        onSelect={onSelect(IssueFilterKeys.Depths)}
        selectedOptions={stateFilters[IssueFilterKeys.Depths]}
      />
      {shouldShowConfidenceFilter && (
        <FeatureFlagRequired flagName={BINARY_DECOMPOSITION}>
          <ConfidenceIssueFilter
            onSelect={onSelect(IssueFilterKeys.Confidence)}
            selectedOptions={stateFilters[IssueFilterKeys.Confidence]}
          />
        </FeatureFlagRequired>
      )}
      <PaidFeatureGate
        planToShow={PREMIUM_COMPLIANCE_PLAN_NAME}
        isRestricted={isFreeTier({
          plan: window.user?.organization?.stripe_plan,
          stripePriceLimit: window.user?.organization?.stripePriceLimit,
        })}
      >
        <TicketIssueFilter selectedOptions={stateFilters[IssueFilterKeys.Ticketed]} />
      </PaidFeatureGate>
      <PaidFeatureGate
        planToShow={PREMIUM_COMPLIANCE_PLAN_NAME}
        isRestricted={isFreeTier({
          plan: window.user?.organization?.stripe_plan,
          stripePriceLimit: window.user?.organization?.stripePriceLimit,
        })}
      >
        <FoundAfterFilter />
      </PaidFeatureGate>

      {getFiltersForProjectType()}
      {getFiltersForCurrentCategory()}
    </div>
  );
};

export default IssuesFilters;
